import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  VehicleTask,
  VehicleTaskService,
  VehicleTaskInput,
  VehicleTaskFilter,
  VehicleTaskDashboardInput,
  VEHICLE_TASK_STATUS,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { Pagination, SortItem } from "@/utils/api/commonApiTypes";

// ============================================
// Types
// ============================================

export interface UseVehicleTasksOptions {
  /**
   * Page size for pagination.
   *
   * This is a **mount-time** configuration value. If you need to change
   * page size after mount, pass it directly to `list()` or `loadMore()`.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply new filters, call `list(newFilters)` explicitly.
   */
  initialFilter?: VehicleTaskFilter;

  /**
   * Sort configuration applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply a new sort, pass it directly to `list()`.
   */
  initialSortBy?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseVehicleTasksReturn {
  // Data state
  task: VehicleTask | null;
  tasks: VehicleTask[];
  isLoading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Dashboard state
  dashboardTasks: VehicleTask[];
  isDashboardLoading: boolean;
  isDashboardLoaded: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  isCompleting: boolean;
  selectedTask: VehicleTask | null;

  // Filter and sort
  filter: VehicleTaskFilter;
  sortBy: SortItem[];

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: VehicleTaskFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: VehicleTaskInput) => Promise<OpResult>;
  update: (id: string, params: VehicleTaskInput) => Promise<OpResult>;
  complete: (id: string) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Dashboard operations
  dashboard: (params?: VehicleTaskDashboardInput) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedTask: (task: VehicleTask | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getTaskById: (id: string | undefined) => VehicleTask | undefined;

  // Derived data helpers
  getByStatus: (status: number) => VehicleTask[];
  pendingTasks: VehicleTask[];
  completedTasks: VehicleTask[];
  overdueTasks: VehicleTask[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_SORT_BY: SortItem[] = [
  { name: "status", order: "ASC" },
  { name: "orderNo", order: "ASC" },
  { name: "dueDate", order: "ASC" },
];

const DEFAULT_FILTER: VehicleTaskFilter = {};

// ============================================
// Hook Implementation
// ============================================

export const useVehicleTasks = (
  options: UseVehicleTasksOptions = {}
): UseVehicleTasksReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilter = DEFAULT_FILTER,
    initialSortBy = DEFAULT_SORT_BY,
    autoLoad = false,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  // These are read-only after mount. They're used by:
  //   - The userId-change effect (for autoLoad re-fetches)
  //   - loadMore / refresh (pageSize and sort)
  // We use refs (not state) so `list()` and other callbacks don't
  // have stale closures, and so effects don't re-fire on every render.
  const pageSizeRef = useRef(pageSize);
  const initialFilterRef = useRef(initialFilter);
  const initialSortByRef = useRef(initialSortBy);

  // Ref to track the last-queried filters for loadMore
  // so we never read stale `filter` state from a closure.
  const lastQueriedFilterRef = useRef<VehicleTaskFilter>(initialFilter);

  // Ref to track the last-queried sort for loadMore/refresh
  const lastQueriedSortRef = useRef<SortItem[]>(initialSortBy);

  // Monotonically increasing call ID to detect stale list() responses.
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [task, setTask] = useState<VehicleTask | null>(null);
  const [tasks, setTasks] = useState<VehicleTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Dashboard state
  const [dashboardTasks, setDashboardTasks] = useState<VehicleTask[]>([]);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [isDashboardLoaded, setIsDashboardLoaded] = useState(false);

  // Search and filters — tracks what was last queried (for display/debugging)
  const [filter, setFilter] = useState<VehicleTaskFilter>(initialFilter);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSortBy);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [selectedTask, setSelectedTask] = useState<VehicleTask | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && tasks.length === 0,
    [isLoaded, tasks.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: VehicleTask) => {
    setTasks((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setTasks((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setTasks((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls so their responses are ignored
    listCallIdRef.current++;

    setTask(null);
    setTasks([]);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setIsCompleting(false);
    setSelectedTask(null);
    setDashboardTasks([]);
    setIsDashboardLoading(false);
    setIsDashboardLoaded(false);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // Get single task
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setIsLoading(true);
      setLastError(null);

      try {
        const result = await VehicleTaskService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as VehicleTask[])[0];
          setTask(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load task"));
        setTask(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load task"));
        setTask(null);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List tasks.
   * - shouldReset=true: replaces tasks array (page 1 load / filter change)
   * - shouldReset=false: appends to existing tasks (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   */
  const list = useCallback(
    async (
      filterOverride: VehicleTaskFilter = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
        // Don't clear tasks here — keep stale data visible while loading.
        // The array is replaced atomically when the response arrives.
        setCurrentPage(1);
      }

      setLastError(null);

      // Store the queried filters/sort so loadMore and refresh can use
      // them via refs — avoids stale closures.
      setFilter(filterOverride);
      lastQueriedFilterRef.current = filterOverride;
      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await VehicleTaskService.list(
          filterOverride,
          paginationParams,
          sortParams
        );

        // Guard: if a newer list() call was initiated while we were waiting,
        // discard this response to avoid overwriting fresher data.
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as VehicleTask[])
            : [];

          if (isAppending) {
            setTasks((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setTasks(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load tasks"));
        setIsLoaded(true);
        if (!isAppending) setTasks([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load tasks"));
        setIsLoaded(true);
        if (!isAppending) setTasks([]);
        return result;
      } finally {
        // Only update loading flags if this call is still the latest.
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setIsLoading(false);
          }
        }
      }
    },
    []
  );

  // Get multiple tasks by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setIsLoading(true);
      setLastError(null);

      try {
        const result = await VehicleTaskService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as VehicleTask[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load tasks"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load tasks"));
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Dashboard Operation
  // ==========================================

  /**
   * Fetch tasks needing attention for the dashboard widget.
   * Returns overdue, due soon, and in-progress tasks with computed
   * urgency fields (isOverdue, daysUntilDue, urgencyStatus).
   * Stored separately from the main tasks list.
   */
  const dashboard = useCallback(
    async (params?: VehicleTaskDashboardInput): Promise<OpResult> => {
      setIsDashboardLoading(true);
      setLastError(null);

      try {
        const result = await VehicleTaskService.dashboard(params);

        if (result.code === OP_RESULT_CODES.OK) {
          const items = (result.data as VehicleTask[]) || [];
          setDashboardTasks(items);
          setIsDashboardLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load dashboard tasks"));
        setIsDashboardLoaded(true);
        setDashboardTasks([]);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load dashboard tasks"));
        setIsDashboardLoaded(true);
        setDashboardTasks([]);
        return result;
      } finally {
        setIsDashboardLoading(false);
      }
    },
    []
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Create task
  const create = useCallback(
    async (params: VehicleTaskInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await VehicleTaskService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTask = (result.data as VehicleTask[])[0];
          setTask(newTask);
          setTasks((prev) => [newTask, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create task"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create task"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update task
  const update = useCallback(
    async (id: string, params: VehicleTaskInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Task ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await VehicleTaskService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedTask = (result.data as VehicleTask[])[0];
          setTask(updatedTask);
          updateOrAddInList(updatedTask);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update task"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update task"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Complete task
  const complete = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Task ID is required for completing"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsCompleting(true);
      setLastError(null);

      try {
        const result = await VehicleTaskService.complete(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const items = Array.isArray(result.data)
            ? (result.data as VehicleTask[])
            : [result.data as VehicleTask];

          // First item is the completed task, rest are newly created (e.g. next recurring)
          const [completedTask, ...newTasks] = items;

          setTasks((prev) => {
            // Update the completed task in place
            const updated = prev.map((t) =>
              t.id === id ? completedTask : t
            );
            // Prepend any new tasks (e.g. next recurring occurrence)
            return newTasks.length > 0 ? [...newTasks, ...updated] : updated;
          });

          setTask(completedTask);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to complete task"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to complete task"));
        return result;
      } finally {
        setIsCompleting(false);
      }
    },
    []
  );

  // Remove task
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Task ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await VehicleTaskService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setTask(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove task"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove task"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove multiple tasks
  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one task ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await VehicleTaskService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setTask(null);
          removeManyFromList(ids);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove tasks"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove tasks"));
        return result;
      }
    },
    [removeManyFromList]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Load more tasks
  // Uses refs for filters and sort to avoid stale closures.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loadingMore, hasMore, currentPage]);

  // Refresh tasks (reset to page 1)
  // Uses refs so it always picks up the last-queried filters/sort.
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  // Lookup helper
  const getTaskById = useCallback(
    (id: string | undefined): VehicleTask | undefined => {
      if (!id) return undefined;
      return tasks.find((item) => item.id === id);
    },
    [tasks]
  );

  const getByStatus = useCallback(
    (status: number): VehicleTask[] => {
      return tasks.filter((t) => t.status === status);
    },
    [tasks]
  );

  const pendingTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status === VEHICLE_TASK_STATUS.TODO ||
          t.status === VEHICLE_TASK_STATUS.IN_PROGRESS
      ),
    [tasks]
  );

  const completedTasks = useMemo(
    () => tasks.filter((t) => t.status === VEHICLE_TASK_STATUS.COMPLETE),
    [tasks]
  );

  const overdueTasks = useMemo(() => {
    const now = new Date();
    return tasks.filter((t) => {
      if (!t.dueDate) return false;
      if (
        t.status === VEHICLE_TASK_STATUS.COMPLETE ||
        t.status === VEHICLE_TASK_STATUS.REMOVED
      ) {
        return false;
      }
      return new Date(t.dueDate) < now;
    });
  }, [tasks]);

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFilterRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFilterRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      task,
      tasks,
      isLoading,
      isLoaded,
      isEmpty,

      // Dashboard state
      dashboardTasks,
      isDashboardLoading,
      isDashboardLoaded,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      isCompleting,
      selectedTask,

      // Filter and sort
      filter,
      sortBy,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      complete,
      remove,
      removeMany,

      // Dashboard operations
      dashboard,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedTask,

      // Helper operations
      reset,

      // Lookup helpers
      getTaskById,

      // Derived data helpers
      getByStatus,
      pendingTasks,
      completedTasks,
      overdueTasks,

      // Error handling
      lastError,
      clearError,
    }),
    [
      task,
      tasks,
      isLoading,
      isLoaded,
      isEmpty,
      dashboardTasks,
      isDashboardLoading,
      isDashboardLoaded,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      isCompleting,
      selectedTask,
      filter,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      complete,
      remove,
      removeMany,
      dashboard,
      loadMore,
      refresh,
      getTaskById,
      getByStatus,
      pendingTasks,
      completedTasks,
      overdueTasks,
      lastError,
      clearError,
      reset,
    ]
  );
};